import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, LogOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";

const navItems = [
  { href: "/", label: "Home" },
  { href: "/upload", label: "Analyze Statement" },
];

export default function Header({ className, ...props }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const location = useLocation();
  const { user, isGuest, signOut, getRemainingAnalyses } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.header
      className={`fixed inset-x-0 top-0 z-50 transition-colors ${
        scrolled
          ? "backdrop-blur border-b border-[rgba(31,76,84,0.12)] bg-[rgba(245,244,242,0.85)]"
          : "bg-[var(--surface-ambient)]/80"
      } ${className || ""}`}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      {...props}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Anti-Slavery Intelligence"
            className="h-10 w-10 rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white object-contain p-1"
          />
          <div className="flex flex-col">
            <span className="text-base font-semibold tracking-tight text-[var(--text-primary)]">
              Anti-Slavery Intelligence
            </span>
            <span className="flex items-center gap-2 text-sm text-[var(--text-soft)]">
              Statement reviewer
              <span className="rounded-full bg-warning-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-900">
                Beta
              </span>
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium transition ${
                  isActive
                    ? "text-[var(--accent)]"
                    : "text-[var(--text-secondary)] hover:text-[var(--accent)]"
                }`}
                aria-current={isActive ? "page" : undefined}
              >
                {item.label}
              </Link>
            );
          })}

          {isGuest ? (
            <Button size="sm" onClick={() => setShowAuthModal(true)}>
              Request access
            </Button>
          ) : (
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                className="gap-2 border-[rgba(31,76,84,0.16)]"
                onClick={() => setShowUserMenu((prev) => !prev)}
                aria-label="User menu"
                aria-expanded={showUserMenu}
                aria-haspopup="true"
              >
                <User className="h-4 w-4" aria-hidden="true" />
                <span className="text-sm">
                  {getRemainingAnalyses()} available
                </span>
              </Button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-2 w-60 rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white p-4 shadow-[0_18px_36px_rgba(15,23,42,0.12)]"
                  >
                    <p className="text-sm font-medium text-[var(--text-primary)]">
                      {user?.email}
                    </p>
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">
                      {getRemainingAnalyses()} of 10 analyses available this month.
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-4 w-full justify-start gap-2"
                      onClick={() => {
                        signOut();
                        setShowUserMenu(false);
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </nav>

        <button
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white text-[var(--text-primary)]"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            className="md:hidden border-t border-[rgba(31,76,84,0.12)] bg-white/95 px-6 pb-6"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="flex flex-col gap-4 py-6">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`rounded-2xl border border-[rgba(31,76,84,0.12)] px-4 py-3 text-sm font-medium ${
                      isActive
                        ? "bg-[var(--accent-light)] text-[var(--accent)]"
                        : "text-[var(--text-primary)]"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                );
              })}

              {isGuest ? (
                <Button onClick={() => setShowAuthModal(true)}>Request access</Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => {
                    signOut();
                    setIsMenuOpen(false);
                  }}
                >
                  Sign out
                </Button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </motion.header>
  );
}
